import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner, Typography } from '@material-tailwind/react';
import {
  ArrowLeftIcon, LayersIcon, ListIcon, MapIcon, PencilIcon, PlusIcon,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import BranchCampusCanvas from '../../components/campus/branch-campus-canvas';
import { useGetBranchById } from '../../custom-hooks/use-branches';
import { useGetBuildingsByBranchId } from '../../custom-hooks/use-buildings';
import { URLS } from '../../router-x/urls';
import ListModeMenu from './list-mode-menu';
import BuildingCrudModal from './building-crud-modal';
import { InterpretOperationalStatus } from '../../utils/ui-interpreters';
import { managementBuildingUrl } from './urls';

const ManagementBranchCampusPage = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { data: branchRes, isLoading: branchLoading } = useGetBranchById(branchId);
  const branch = branchRes?.data?.branch;
  const { data: buildings, isLoading: buildingsLoading, isError } = useGetBuildingsByBranchId(branchId);

  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [buildingModalOpen, setBuildingModalOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [viewMode, setViewMode] = useState('map');

  const branchLabel = branch?.branchName || branch?.name || 'Branch';

  useEffect(() => {
    if (!selectedBuilding?._id || !buildings) return;
    const fresh = buildings.find((b) => b._id === selectedBuilding._id);
    if (fresh) setSelectedBuilding(fresh);
    else setSelectedBuilding(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildings]);

  const openAddBuilding = () => {
    setEditingBuilding(null);
    setBuildingModalOpen(true);
  };

  const openManageBuilding = () => {
    if (!selectedBuilding) return;
    setEditingBuilding(selectedBuilding);
    setBuildingModalOpen(true);
  };

  const enterFloors = (building = selectedBuilding) => {
    if (!building?._id) return;
    navigate(managementBuildingUrl(branchId, building._id));
  };

  const sortedBuildings = useMemo(
    () => [...(buildings || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [buildings]
  );

  if (branchLoading) {
    return (
      <div className="m-5 p-5 border rounded-3xl flex justify-center py-16">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div>
      <div className="m-5 p-5 border rounded-3xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div>
            <Button
              variant="text"
              size="sm"
              className="rounded-full flex items-center gap-2 mb-2 -ml-2"
              onClick={() => navigate(URLS.ADMIN.MANAGEMENT)}
            >
              <ArrowLeftIcon size={16} />
              Back to branches
            </Button>
            <Typography variant="small" className="text-orange-600 font-medium uppercase tracking-wide text-[11px]">
              Branch · {branchLabel}
            </Typography>
            <h1 className="md:text-2xl font-thin">Buildings</h1>
            <Typography variant="small" className="text-gray-500 mt-1">
              {[branch?.address, branch?.city].filter(Boolean).join(', ')}
            </Typography>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('map')}
                className={`rounded-full px-3 py-1 text-xs flex items-center gap-1 ${viewMode === 'map' ? 'bg-gray-900 text-white' : 'text-gray-600'}`}
              >
                <MapIcon size={12} /> Map
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`rounded-full px-3 py-1 text-xs flex items-center gap-1 ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'text-gray-600'}`}
              >
                <ListIcon size={12} /> List
              </button>
            </div>
            <ListModeMenu branchId={branchId} branchName={branchLabel} />
            <Button
              size="sm"
              className="rounded-full flex items-center gap-2 normal-case font-normal"
              onClick={openAddBuilding}
            >
              <PlusIcon size={14} />
              Add building
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="text-center py-8 text-red-500">Error loading buildings.</div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 min-w-0">
              {viewMode === 'map' ? (
                <BranchCampusCanvas
                  buildings={buildings || []}
                  isLoading={buildingsLoading}
                  selectedBuildingId={selectedBuilding?._id}
                  onSelectBuilding={setSelectedBuilding}
                />
              ) : (
                <div className="rounded-2xl border overflow-hidden">
                  <div className="px-4 py-3 bg-orange-50 border-b text-sm text-orange-900">
                    Showing buildings in <span className="font-semibold">{branchLabel}</span> only
                  </div>
                  {buildingsLoading ? (
                    <div className="flex justify-center py-12"><Spinner /></div>
                  ) : sortedBuildings.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">No buildings in this branch yet.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Building</th>
                          <th className="px-4 py-3">Code</th>
                          <th className="px-4 py-3">Floors</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sortedBuildings.map((building) => (
                          <tr
                            key={building._id}
                            className={`hover:bg-gray-50 cursor-pointer ${selectedBuilding?._id === building._id ? 'bg-orange-50/50' : ''}`}
                            onClick={() => setSelectedBuilding(building)}
                          >
                            <td className="px-4 py-3 font-medium">{building.name}</td>
                            <td className="px-4 py-3 text-gray-500">{building.buildingCode}</td>
                            <td className="px-4 py-3">{building.totalFloors || 0}</td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                size="sm"
                                className="rounded-full normal-case text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  enterFloors(building);
                                }}
                              >
                                Floors
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {selectedBuilding && (
              <div className="w-full lg:w-80 shrink-0 rounded-2xl border bg-white p-4 h-fit lg:sticky lg:top-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <Typography variant="h6" className="truncate">
                      {selectedBuilding.name}
                    </Typography>
                    <Typography variant="small" className="text-gray-500">
                      {selectedBuilding.buildingCode}
                    </Typography>
                  </div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-700 text-sm"
                    onClick={() => setSelectedBuilding(null)}
                  >
                    Close
                  </button>
                </div>
                <Typography variant="small" className="text-orange-700 mb-3">
                  In branch: {branchLabel}
                </Typography>
                <div className="flex items-center gap-2 mb-3">
                  {InterpretOperationalStatus(selectedBuilding.status)}
                  <Typography variant="small" className="text-gray-500">
                    {selectedBuilding.totalFloors || 0} floors · {selectedBuilding.floorsOccupied ?? '—'} occupied
                  </Typography>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outlined"
                    className="rounded-full flex items-center justify-center gap-2 normal-case"
                    onClick={openManageBuilding}
                  >
                    <PencilIcon size={14} />
                    Edit building
                  </Button>
                  <Button
                    size="sm"
                    className="rounded-full flex items-center justify-center gap-2 normal-case"
                    onClick={() => enterFloors()}
                  >
                    <LayersIcon size={14} />
                    Manage floors
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <BuildingCrudModal
        isOpen={buildingModalOpen}
        onClose={() => {
          setBuildingModalOpen(false);
          setEditingBuilding(null);
        }}
        branchId={branchId}
        building={editingBuilding}
        existingCount={buildings?.length || 0}
      />
    </div>
  );
};

export default ManagementBranchCampusPage;
